import { useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SnackbarUtils from 'utils/SnackbarUtils';
import {
  changeModalProps,
  closeModal,
  openModal,
} from '../../store/modules/ModalActions';
import { getContentTypeByUrl, getFileNameEncoded } from '../../utils/FileUtils';
import { getMergedClassNames } from '../../utils/HTMLUtils';
import ProgressModal from '../ProgressModal/ProgressModal';
import NewDownloadIcon from 'assets/icons/NewDownloadIcon';
import { RootState } from 'store';
interface CustomDownloadButtonProps {
  id?: string;
  className?: string;
  label?: string | React.ReactNode;
  downloadingLabel?: string;
  hideLabel?: boolean;
  showIcon?: boolean;
  buttonComponent: React.ElementType;
  buttonComponentProps?: Record<string, unknown>;
  disabled?: boolean;
  fileName: string;
  downloadAction: (data: unknown, onProgress: (percent: number) => void, onSuccess: (resp: unknown) => void, onError: (error: unknown) => void) => void;
  downloadActionData: number | string | object;
  downloadActionStartCallback?: () => void;
  downloadActionProgressCallback?: (percent: number) => void;
  downloadActionSuccessCallback?: (resp: unknown) => void;
  downloadActionErrorCallback?: (error: unknown) => void;
  feedbackOnModal?: boolean;
  feedbackOnModalTitle?: string;
}

const CustomDownloadButton = ({
  className,
  label,
  downloadingLabel,
  hideLabel,
  showIcon,
  disabled,
  buttonComponent: ButtonComponent,
  buttonComponentProps = {},
  fileName,
  downloadAction,
  downloadActionData,
  downloadActionStartCallback,
  downloadActionProgressCallback,
  downloadActionSuccessCallback,
  downloadActionErrorCallback,
  feedbackOnModal,
  feedbackOnModalTitle,
}: CustomDownloadButtonProps) => {
  const [downloading, setDownloading] = useState(false);
  const [percentState, setPercentState] = useState(0);
  const modalKey = useRef<string | null>(null);
  const languageValues = useSelector((state: RootState) => state.languages.languageValues);
  const dispatch = useDispatch();
  const getFileName = useCallback(() => getFileNameEncoded(fileName), [fileName]);

  const onProgress = useCallback((percent) => {
    if (downloadActionProgressCallback) {
      downloadActionProgressCallback(percent);
    }
    setPercentState(percent);
    if (feedbackOnModalIsEnabled() && modalKey.current) {
      dispatch(changeModalProps(modalKey.current, { percent: percent }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadActionProgressCallback, dispatch]);

  const onSuccess = useCallback((resp) => {
    if (downloadActionSuccessCallback) {
      downloadActionSuccessCallback(resp);
    }
    const contentType = getContentTypeByUrl(fileName) || 'application/octet-stream';
    try {
      const blob = new Blob([resp.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getFileName();
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (contentType === 'application/pdf') {
        setTimeout(() => {
          window.open(url, '_blank');
        }, 1000);
      }
    } catch (e) {
      SnackbarUtils.enqueueSnackbar({
        message: 'Erro baixar arquivo:', e,
        options: { variant: 'error' },
      });
    }

    resetDownloadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadActionSuccessCallback, fileName, getFileName]);

  const onError = useCallback((error) => {
    if (downloadActionErrorCallback) {
      downloadActionErrorCallback(error);
    }
    SnackbarUtils.enqueueSnackbar({
      message: error?.response?.statusText || languageValues.downloadFileError.replace('${1}', getFileName()),
      options: { variant: 'error' },
    });
    resetDownloadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadActionErrorCallback, languageValues.downloadFileError, getFileName]);

  const toStartDownload = useCallback(() => {
    if (downloadActionStartCallback) {
      downloadActionStartCallback();
    }
    setDownloading(true);
    return new Promise<void>((resolve, reject) => {
      downloadAction(
        downloadActionData,
        onProgress,
        (resp) => {
          resolve();
          onSuccess(resp);
        },
        (error) => {
          reject(error);
          onError(error);
        }
      );
    });
  }, [downloadAction, downloadActionData, onProgress, onSuccess, onError, downloadActionStartCallback]);

  const toDownload = useCallback(() => {
    return new Promise((resolve) => {
      if (feedbackOnModalIsEnabled()) {
        const modalAction = dispatch(openModal(ProgressModal, {
          title: feedbackOnModalTitle || 'Título indefinido',
          percent: 0,
        }));
        if (!modalAction || !modalAction.payload || !modalAction.payload.key) {
          SnackbarUtils.enqueueSnackbar({
            message: 'Erro ao abrir modal de progresso.',
            options: { variant: 'error' },
          });
          return;
        }

        modalKey.current = modalAction.payload.key;
        setPercentState(0);
        toStartDownload().then(resolve);
      } else {
        toStartDownload().then(resolve);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackOnModalTitle, toStartDownload, dispatch]);

  const resetDownloadState = () => {
    setPercentState(0);
    setDownloading(false);
    if (feedbackOnModalIsEnabled() && modalKey.current) {
      dispatch(closeModal(modalKey.current));
      modalKey.current = null;
    }
  };

  const feedbackOnModalIsEnabled = () => feedbackOnModal && typeof feedbackOnModalTitle === 'string';

  const getButtonComponent = () => {
    const componentProps = {
      ...buttonComponentProps,
      disabled: disabled || buttonComponentProps.disabled || downloading,
      className: getMergedClassNames([buttonComponentProps.className, className]),
      label: getLabel(),
      onClick: toDownload,
    };

    const icon = showIcon ? <NewDownloadIcon color="#FFFFFF" /> : null;

    if (!hideLabel) {
      componentProps.label = (
        <>
          {getLabel()}
          <span style={{ marginLeft: 8, marginBottom: 7 }}>{icon}</span>
        </>
      );
    }

    return <ButtonComponent {...componentProps} style={{background: '#ffeb8c'}} />;
  };

  const getLabel = () => {
    const defaultLabel = label || languageValues.downloadLabel;
    return downloading
      ? (downloadingLabel || languageValues.downloadingLabel.replace('${1}', String(Math.round(percentState * 100))))
      : defaultLabel;
  };

  return getButtonComponent();
};

export default CustomDownloadButton;
